import React, { Fragment } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { t } from '../../../../../modules/infra/i18n';
import Icon, { GLYPHS } from '../../../../common/icon';
import { ELIGIBILITY_STEP_STATUS, END_OF_PAY_PERIOD_DAY_OPTIONS } from '../constants';
import AutoPayoutEligibilityStatus from './AutoPayoutEligibilityStatus';
import CheckboxSwitch from '../../../../common/checkboxSwitch';
import { formatPercentage } from '../../../../common/Percentage';
import Tooltip from '../../../../common/tooltip';
import Select from '../../../../common/select';
import { eligibilityStatus } from '../propTypes';
import EligibilityStep from './EligibilityStep';

const AutoPayout = ({
  isShopViewActive,
  howToEnableIsOn,
  howToEnableToggle,
  eligibilitySteps,
  overallEligibilityStatus,
  barbersWithIssues,
  isAutoPayoutEnabled,
  isAutoPayoutPaused,
  isAutoPayoutSettingsEditable,
  handleSubmit,
  dailyPayroll,
  dailyPayrollFee,
  commissionSplits,
  commissionSplitsFee,
  commissionSplitsShopPaysFees,
  isSaving,
  reset,
}) => (
  <div className="c-wrapper">
    <div className="u-max-width-710 u-margin-auto">
      <div
        display-if={isShopViewActive && isAutoPayoutPaused}
        className="c-autopayout__status c-autopayout__status-critical u-mb"
      >
        <div className="c-autopayout__status-icon c-autopayout__status-critical">i</div>
        <span>{t('settings:auto_payout.paused_message')}</span>
      </div>
    </div>
    <div className="c-inner-wrapper u-max-width-710 u-br-12">
      <h2 className="c-heading c-heading--medium c-heading--dark u-type--semibold">
        {t('settings:auto_payout.how_it_works')}
      </h2>
      <p className="c-heading--small c-autopayout__description u-mt-">{t('settings:auto_payout.description')}</p>

      <div className="c-separator u-mt u-mb--" />

      <button
        type="button"
        onClick={howToEnableToggle}
        className="c-btn c-btn--transparent u-ph0 c-autopayout__section-header"
      >
        <div className={classnames('c-settings-section__header-icon u-inline-block', { 'is-active': howToEnableIsOn })}>
          <Icon glyph={GLYPHS.chevronDropdown} size="micro" />
        </div>
        <span className="u-ml-- u-type--uppercase">{t('settings:auto_payout.how_to_enable')}</span>
      </button>

      <div display-if={howToEnableIsOn} className="c-autopayout-steps">
        {eligibilitySteps.map((step) => (
          <EligibilityStep key={step.id} stepId={step.id} status={step.status} barbersWithIssues={barbersWithIssues} />
        ))}
      </div>

      <AutoPayoutEligibilityStatus
        status={overallEligibilityStatus}
        isAutoPayoutPaused={isAutoPayoutPaused}
        isAutoPayoutEnabled={isAutoPayoutEnabled}
        display-if={
          overallEligibilityStatus !== ELIGIBILITY_STEP_STATUS.DONE || !isAutoPayoutEnabled || howToEnableIsOn
        }
      />

      <div className="c-autopayout__section-separator" />

      <form onSubmit={handleSubmit}>
        <fieldset
          className={classnames('c-autopayout__section', {
            'c-autopayout__section-disabled': !isAutoPayoutSettingsEditable && isShopViewActive,
          })}
          disabled={!isAutoPayoutSettingsEditable || !isShopViewActive}
        >
          <h2 className="c-heading c-heading--medium c-heading--dark u-type--semibold">
            {t('settings:auto_payout.auto_commission_splits')}
          </h2>
          <p className="c-heading--small c-autopayout__description u-mt-">
            {t('settings:auto_payout.commission_splits_description')}
          </p>
          <div className="o-flexbox o-flexbox--content-align-end u-mt">
            <CheckboxSwitch
              name="commissionSplits"
              id="commissionSplits"
              label={commissionSplits ? t('settings:auto_payout.enabled') : t('settings:auto_payout.disabled')}
            />
            <div>
              <span className={classnames('c-label-new', { 'c-autopayout__text-black': commissionSplits })}>
                {t('settings:auto_payout.auto_commission_split_fee')} {formatPercentage(commissionSplitsFee, 2)}
              </span>
              <Tooltip
                className="c-info-tooltip-new c-info-tooltip-new--form"
                text={t('settings:auto_payout.fee_tooltip')}
              />
            </div>
          </div>

          <div className="c-separator u-mv" />

          <h2 className="c-heading c-heading--small c-heading--dark u-type--semibold">
            {t('settings:auto_payout.shop_covered_fees')}
          </h2>
          <p className="c-heading--small c-autopayout__description u-mt- u-mb">
            {t('settings:auto_payout.shop_covered_fees_description')}
          </p>
          <CheckboxSwitch
            name="commissionSplitsShopPaysFees"
            id="commissionSplitsShopPaysFees"
            label={
              commissionSplitsShopPaysFees ? t('settings:auto_payout.enabled') : t('settings:auto_payout.disabled')
            }
            disabled={!commissionSplits}
          />

          <div className="c-separator u-mv" />

          <h2 className="c-heading c-heading--small c-heading--dark u-type--semibold">
            {t('settings:auto_payout.payment_date_time')}
          </h2>
          <p className="c-heading--small c-autopayout__description u-mt- u-mb">
            {t('settings:auto_payout.payment_date_description')}
          </p>
          <div className="o-flexbox layout">
            <div className="layout__item u-1/2-medium-and-up">
              <div className="c-label-new">{t('settings:auto_payout.end_of_pay_period_day')}</div>
              <Select
                className="c-select"
                name="commissionPayoutDayOfWeek"
                parse={(val) => parseInt(val, 10)}
                options={END_OF_PAY_PERIOD_DAY_OPTIONS}
                empty={t('settings:auto_payout.please_select')}
                disabled={!commissionSplits}
              />
            </div>
            <div className="layout__item u-1/2-medium-and-up">
              <div className="c-label-new">{t('settings:auto_payout.length_of_pay_period')}</div>
              <input
                className="c-input c-autopayout__disabled-input"
                value={t('settings:auto_payout.one_week')}
                disabled
              />
            </div>
          </div>

          <div className="c-separator u-mb" />

          <h2 className="c-heading c-heading--small c-heading--dark u-type--semibold">
            {t('settings:auto_payout.cash_goes_to_barbers')}
          </h2>
          <p className="c-heading--small c-autopayout__description u-mt- u-mb">
            {t('settings:auto_payout.cash_goes_description')}
          </p>
          <div className="u-flex">
            <CheckboxSwitch
              name="commissionSplitsCashToBarbersServices"
              id="commissionSplitsCashToBarbersServices"
              label={t('settings:auto_payout.for_services')}
              disabled={!commissionSplits}
            />
            <Tooltip
              className="c-info-tooltip-new c-info-tooltip-new--form u-ml0 u-mr u-mt--"
              text={t('settings:auto_payout.services_tooltip')}
            />

            <CheckboxSwitch
              name="commissionSplitsCashToBarbersProducts"
              id="commissionSplitsCashToBarbersProducts"
              label={t('settings:auto_payout.for_products')}
              disabled={!commissionSplits}
            />
            <Tooltip
              className="c-info-tooltip-new c-info-tooltip-new--form u-ml0 u-mt--"
              text={t('settings:auto_payout.products_tooltip')}
            />
          </div>
        </fieldset>

        <div className="c-autopayout__section-separator" />

        <fieldset
          className={classnames('c-autopayout__section', {
            'c-autopayout__section-disabled': !isAutoPayoutSettingsEditable && isShopViewActive,
          })}
          disabled={!isAutoPayoutSettingsEditable || !isShopViewActive}
        >
          <h2 className="c-heading c-heading--medium c-heading--dark u-type--semibold">
            {t('settings:auto_payout.daily_shop_payouts')}
          </h2>
          <p className="c-heading--small c-autopayout__description u-mt-">
            {t('settings:auto_payout.daily_payouts_description')}
          </p>
          <div className="o-flexbox o-flexbox--content-align-end u-mt">
            <CheckboxSwitch
              name="dailyPayroll"
              id="dailyPayroll"
              label={dailyPayroll ? t('settings:auto_payout.enabled') : t('settings:auto_payout.disabled')}
            />
            <div>
              <span className={classnames('c-label-new', { 'c-autopayout__text-black': dailyPayroll })}>
                {t('settings:auto_payout.daily_shop_payout_fee')} {formatPercentage(dailyPayrollFee, 2)}
              </span>
              <Tooltip
                className="c-info-tooltip-new c-info-tooltip-new--form"
                text={t('settings:auto_payout.daily_fee_tooltip')}
              />
            </div>
          </div>
        </fieldset>

        <div className="c-autopayout__section-separator" />

        <div className="u-flex o-flexbox--justify-end">
          <button type="button" className="c-btn c-btn--grey u-width-120 u-mr--" onClick={reset}>
            {t('settings:auto_payout.cancel')}
          </button>
          <button type="submit" className="c-btn u-width-120" disabled={!isAutoPayoutSettingsEditable || isSaving}>
            {t('settings:auto_payout.save')}
          </button>
        </div>
      </form>
    </div>
  </div>
);

AutoPayout.propTypes = {
  isShopViewActive: PropTypes.bool.isRequired,
  howToEnableIsOn: PropTypes.bool.isRequired,
  howToEnableToggle: PropTypes.func.isRequired,
  eligibilitySteps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: eligibilityStatus.isRequired,
    })
  ).isRequired,
  overallEligibilityStatus: eligibilityStatus.isRequired,
  barbersWithIssues: PropTypes.arrayOf(PropTypes.object).isRequired,
  isAutoPayoutPaused: PropTypes.bool.isRequired,
  isAutoPayoutEnabled: PropTypes.bool.isRequired,
  isAutoPayoutSettingsEditable: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  dailyPayroll: PropTypes.bool.isRequired,
  dailyPayrollFee: PropTypes.number.isRequired,
  commissionSplits: PropTypes.bool.isRequired,
  commissionSplitsFee: PropTypes.number.isRequired,
  commissionSplitsShopPaysFees: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
};

export default AutoPayout;
